import './Dashboard.css';
import { Box, Typography, Select, MenuItem, FormControl } from '@mui/material';
import Layout from '../../../components/Layout';
import TeacherNavbar from '../../../components/TeacherNavbar/TeacherNavbar';
import UpcomingSessionTable from '../../../components/Dashboard/UpcomingSessionTable/UpcomingSessionTable';
import Revenue from '../../../components/Dashboard/Revenue/Revenue';
import StatsCard from '../../../components/Dashboard/StatsCard/StatsCard';
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../../utils/tokenUtils';
import { format, parseISO } from 'date-fns';
import SessionCategory from '../../../components/Dashboard/SessionCategory/SessionCategory';
import DropdownIcon from '../../../assets/ScheduleIcon/dropdown.svg';
import LoadingScreen from '../../../components/LoadingIcon/LoadingScreen';
import { DashboardStatsSkeleton } from '../../../components/SkeletonEffect/SkeletonEffect';
import InstructorInsights from '../../../components/Dashboard/InstructorInsights/InstructorInsights';

const Dashboard = () => {
  const [timePeriod, setTimePeriod] = useState('1_month');
  const [dashboardStats, setDashboardStats] = useState({
    time_frame: '1_month',
    total_sessions_today: 0,
    sessions_growth_percent: 0.0,
    total_students: 0,
    students_growth_percent: 0.0,
    total_earnings: 0.0,
    earnings_growth_percent: 0.0,
    total_hours_spent: 0,
    hours_growth_percent: 0.0,
    total_availability: {
      one_on_one: 0,
      group: 0,
      prime: 0,
      total: 0,
    },
  });
  const [availabilityData, setAvailabilityData] = useState({
    one_on_one: 0,
    group: 0,
    prime: 0,
    total: 0,
  });
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleTimePeriodChange = event => {
    const newValue = event.target.value;
    setTimePeriod(newValue);
    // Only stats should show skeleton on time frame change
    setStatsLoading(true);
  };

  const getComparisonText = timeFrame => {
    switch (timeFrame) {
      case '1_week':
        return 'vs last week';
      case '2_weeks':
        return 'vs previous 2 weeks';
      case '1_month':
        return 'vs last month';
      case '3_months':
        return 'vs previous 3 months';
      case '6_months':
        return 'vs previous 6 months';
      case '1_year':
        return 'vs last year';
      default:
        return 'vs last month';
    }
  };

  const fetchInitialData = async () => {
    try {
      setStatsLoading(true);
      // Fetch stats and upcoming sessions (only once on initial load)
      const [statsResponse, sessionsResponse] = await Promise.all([
        axiosInstance.get(`/teachers/stats?time_frame=${timePeriod}`),
        axiosInstance.get('/teachers/sessions?status=upcoming&offset=0&limit=10'),
      ]);

      // Stats for cards
      setDashboardStats(statsResponse.data);
      // Capture availability only once for donut (decoupled from main time frame)
      if (statsResponse.data?.total_availability) {
        setAvailabilityData(statsResponse.data.total_availability);
      }

      // Upcoming sessions (decoupled from main time frame; fetched once)
      if (sessionsResponse?.data?.results?.length > 0) {
        const transformedSessions = sessionsResponse.data.results.map(session => {
          const startTimeUTC = parseISO(session.start_time);
          const startTimeLocal = new Date(
            startTimeUTC.getTime() - startTimeUTC.getTimezoneOffset() * 60000
          );
          const endTimeLocal = new Date(
            startTimeLocal.getTime() + session.length_in_minutes * 60000
          );
          const date = format(startTimeLocal, 'EEE d MMM, yyyy');
          const startFormat = format(startTimeLocal, 'h:mm a');
          const endFormat = format(endTimeLocal, 'h:mm a');
          const timing = `${startFormat} - ${endFormat}`;
          const student = session.students[0];
          const studentName =
            session.students.length === 1
              ? student.user.name
              : `${student.user.name} +${session.students.length - 1}`;
          const now = new Date();
          const canJoin =
            startTimeLocal.getTime() - now.getTime() <= 15 * 60 * 1000 && startTimeLocal > now;

          return {
            id: session.id,
            studentName,
            avatarUrl: student.user.profile_image,
            date,
            timing,
            subject: session.subject,
            level: student.school_level ? student.school_level.replace('_', ' ') : 'N/A',
            sessionType: session.session_type,
            canJoin,
          };
        });
        setUpcomingSessions(transformedSessions);
      } else {
        setUpcomingSessions([]);
      }

      setError(null);
    } catch (error) {
      console.error('Error fetching initial dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setInitialLoading(false);
      setStatsLoading(false);
    }
  };

  const fetchStatsOnly = async () => {
    try {
      const statsResponse = await axiosInstance.get(`/teachers/stats?time_frame=${timePeriod}`);
      // Update only stats-related values; keep donut availability unchanged
      setDashboardStats(prev => ({
        ...statsResponse.data,
        total_availability: prev.total_availability,
      }));
      setError(null);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setError('Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (initialLoading) {
      fetchInitialData();
    } else {
      fetchStatsOnly();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timePeriod]);

  if (initialLoading) {
    return (
      <Layout title="Klass Online | Dashboard">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <TeacherNavbar />
            <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px] flex justify-center items-center">
              <LoadingScreen />
            </Box>
          </Box>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Klass Online | Dashboard">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <TeacherNavbar />
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <Box className="p-[1.5rem]">
              <Box className="flex justify-between items-center mb-4">
                <Typography variant="h5" className="dashboard-title">
                  Dashboard Overview
                </Typography>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={timePeriod}
                    onChange={handleTimePeriodChange}
                    displayEmpty
                    className="time-period-select-container"
                    IconComponent={() => (
                      <Box
                        component="img"
                        src={DropdownIcon}
                        alt="dropdown"
                        sx={{ mr: 1, mt: 1, pointerEvents: 'none' }}
                      />
                    )}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '0 0 8px 8px',
                        },
                      },
                    }}
                  >
                    <MenuItem value="1_week" className="time-period-option">
                      Last Week
                    </MenuItem>
                    <MenuItem value="2_weeks" className="time-period-option">
                      Last 2 Weeks
                    </MenuItem>
                    <MenuItem value="1_month" className="time-period-option">
                      Last Month
                    </MenuItem>
                    <MenuItem value="3_months" className="time-period-option">
                      Last 3 Months
                    </MenuItem>
                    <MenuItem value="6_months" className="time-period-option">
                      Last 6 Months
                    </MenuItem>
                    <MenuItem value="1_year" className="time-period-option">
                      Last Year
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Stats Cards */}
              {statsLoading ? (
                <DashboardStatsSkeleton />
              ) : (
                <Box className="flex flex-wrap gap-3 mb-4">
                  <Box className="flex-[0_0_calc(25%-12px)] min-w-[250px]">
                    <StatsCard
                      title="Total Session"
                      value={dashboardStats.total_sessions_today}
                      changePercentage={dashboardStats.sessions_growth_percent}
                      isPositive={dashboardStats.sessions_growth_percent >= 0}
                      comparisonText={getComparisonText(dashboardStats.time_frame)}
                    />
                  </Box>
                  <Box className="flex-[0_0_calc(25%-12px)] min-w-[250px]">
                    <StatsCard
                      title="Total Learners"
                      value={dashboardStats.total_students}
                      changePercentage={dashboardStats.students_growth_percent}
                      isPositive={dashboardStats.students_growth_percent >= 0}
                      comparisonText={getComparisonText(dashboardStats.time_frame)}
                    />
                  </Box>
                  <Box className="flex-[0_0_calc(25%-12px)] min-w-[250px]">
                    <StatsCard
                      title="Total Earnings"
                      value={dashboardStats.total_earnings}
                      changePercentage={dashboardStats.earnings_growth_percent}
                      isPositive={dashboardStats.earnings_growth_percent >= 0}
                      comparisonText={getComparisonText(dashboardStats.time_frame)}
                      suffix="Koins"
                    />
                  </Box>
                  <Box className="flex-[0_0_calc(25%-12px)] min-w-[250px]">
                    <StatsCard
                      title="Total Hours Spent"
                      value={dashboardStats.total_hours_spent}
                      changePercentage={dashboardStats.hours_growth_percent}
                      isPositive={dashboardStats.hours_growth_percent >= 0}
                      comparisonText={getComparisonText(dashboardStats.time_frame)}
                      suffix="hrs"
                    />
                  </Box>
                </Box>
              )}

              {/* Revenue and Session Category */}
              <Box className="flex flex-wrap gap-3 mb-4">
                <Box className="flex-1 min-w-[400px]">
                  {/* Revenue manages its own period tabs and fetching */}
                  <Revenue />
                </Box>
                <Box className="w-[400px] min-w-[300px]">
                  {/* Donut decoupled from main time frame; uses availability captured on initial load */}
                  <SessionCategory availabilityData={availabilityData} />
                </Box>
              </Box>

              <InstructorInsights />

              {/* Upcoming Sessions Table (decoupled from main time frame) */}
              <UpcomingSessionTable sessions={upcomingSessions} />

              {error && (
                <Box mt={2}>
                  <Typography color="error">{error}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Dashboard;
